import classnames from 'classnames'
import { Layout } from 'components/layout'
import { Typography } from 'components/typography'
import { WithAds } from 'components/with-ads'
import { LESSON_NAME, LESSON_NAMES, LESSON_PAGE_EXCLUSION, LESSON_TITLE } from 'constants/lessons'
import { PG } from 'constants/pg'
import { PAGES } from 'constants/routes'
import { useLoadInitialData } from 'hooks/database/use-load-initial-data'
import { useLoadStats } from 'hooks/database/use-load-stats'
import { useAppDispatch } from 'hooks/use-app-dispatch'
import { useAppSelector } from 'hooks/use-app-selector'
import { PageRoute } from 'interface/routes'
import { setLocalStoreLessonRules } from 'local-store/lesson'
import _kebabCase from 'lodash/kebabCase'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useAuthUser } from 'next-firebase-auth'
import { useCallback } from 'react'
import { pgSlices } from 'store/slices/pg-slice'
import { selectRecentLessonsStats } from 'store/slices/stat/stat'
import {
  selectAiSubscriptionLoading,
  selectIsAiSubscriptionActiveAndNotAdFreeOnly,
} from 'store/slices/subscription/subscription'
import styled, { css } from 'styled-components'
import { pageCenter } from 'styles/page-center.styles'
import { Button } from 'ui-kit/button'
import { Divider } from 'ui-kit/divider'

import { urlWithSiteOrigin } from '../../utils/url-with-site-origin'
import {
  Banner,
  BannerContent,
  BannerDescription,
  BannerTitle,
  LessonAccuracy,
  LessonCard,
  LessonCardContent,
  LessonCardDescription,
  LessonCardHeader,
  LessonCardStats,
  LessonsWrapper,
  LessonWpm,
  StartButton,
} from './lessons.styles'
import { getLessonDescription } from './utils/get-lesson-description'
import { getLessonIcon } from './utils/get-lesson-Icon'

const Wrapper = styled.div`
  ${pageCenter};
  padding: 0 1.75rem;

  ${({ theme }) => css`
    ${theme.breakpoints.down.xl} {
      max-width: 1200px;
    }
  `}

  ${({ theme }) => css`
    ${theme.breakpoints.down.lg} {
      max-width: 900px;
    }
  `}

  ${({ theme }) => css`
    ${theme.breakpoints.down.md} {
      padding: 0 1rem;
      max-width: 700px;
    }
  `}

  ${({ theme }) => css`
    ${theme.breakpoints.down.sm} {
      padding: 0 0.75rem;
      max-width: 100%;
    }
  `}
`

export const Lessons = () => {
  useLoadInitialData()
  useLoadStats()

  const router = useRouter()

  const authUser = useAuthUser()
  const authLoading = !authUser.clientInitialized

  const mainPgSlice = pgSlices[PG.MAIN_PG]
  const dispatch = useAppDispatch()

  const aiSubscriptionLoading = useAppSelector(selectAiSubscriptionLoading)
  const isAiSubscriber = useAppSelector(selectIsAiSubscriptionActiveAndNotAdFreeOnly)

  const lessonsStats = useAppSelector(selectRecentLessonsStats)

  const generateLesson = useCallback(
    (lessonName: LESSON_NAME) => {
      // Set local storage for loading correct lesson name on reload
      setLocalStoreLessonRules({
        pageRoute: PAGES['/typing-practice/'] as PageRoute,
        rules: { lessonName },
      })

      dispatch(
        mainPgSlice.actions.handleRestart({
          pgName: PG.MAIN_PG,
          rules: { lessonName },
        })
      )
    },
    [dispatch, mainPgSlice.actions]
  )

  return (
    <Layout
      seoProps={{
        title: 'Custom typing lessons, common words, n-grams, weak words',
        canonicalUrl: urlWithSiteOrigin('/lessons/'),
        pageUrl: urlWithSiteOrigin('/lessons/'),
      }}
      authLoading={authLoading}
      uid={authUser.id}
      signOut={authUser.signOut}
    >
      <Wrapper>
        <Divider noBar wrapperThickness="1rem" />

        <Banner>
          <BannerContent>
            <BannerTitle>Master Your Typing Skills</BannerTitle>
            <BannerDescription>
              Choose from our comprehensive collection of typing lessons designed to improve your speed, accuracy, and
              technique. Whether you&apos;re a beginner or advanced typist, we have the perfect practice for you.
            </BannerDescription>
          </BannerContent>
        </Banner>

        <Divider noBar />

        <WithAds>
          <LessonsWrapper>
            {Object.keys(LESSON_TITLE)
              .filter((lesson) => !LESSON_PAGE_EXCLUSION.includes(lesson as LESSON_NAME))
              .map((lesson, index: number) => {
                const rowId = _kebabCase(LESSON_TITLE[lesson as LESSON_NAME])
                const { avgWpm, avgAccuracy, totalDuration } = lessonsStats[lesson as LESSON_NAME] || {}

                const isAiLesson = lesson === LESSON_NAMES.AI

                let href = PAGES['/typing-practice/']

                const shouldWait = isAiLesson && aiSubscriptionLoading
                const shouldRedirectToAiLandingPage = isAiLesson && !isAiSubscriber && authUser.id

                if (shouldWait) href = '#'
                if (shouldRedirectToAiLandingPage) href = PAGES['/typing-mentor-coach/']

                const shouldRedirectToSignInPage = isAiLesson && !authLoading && !authUser.id && !aiSubscriptionLoading

                if (shouldRedirectToSignInPage) href = PAGES['/login/']

                return (
                  <Link
                    href={href}
                    key={index}
                    prefetch={false}
                    onClick={() => {
                      if (shouldWait) return

                      // redirect non-subscribers to ai subscription page
                      if (shouldRedirectToAiLandingPage) {
                        router.push(PAGES['/typing-mentor-coach/'])
                        return
                      }

                      generateLesson(lesson as LESSON_NAME)
                    }}
                  >
                    <LessonCard
                      data-cy={`lessons-${rowId}`}
                      className={classnames(`gtm-lesson-${rowId}`)}
                      isHighlighted={isAiLesson}
                    >
                      <LessonCardContent>
                        <LessonCardHeader>
                          <span style={{ fontSize: '2rem', marginRight: '0.5rem', display: 'inline-block' }}>
                            {getLessonIcon(lesson as LESSON_NAME)}
                          </span>
                          <Typography className="lesson-title" bold variant="heading3">
                            {LESSON_TITLE[lesson as LESSON_NAME].replace('Most ', '')}
                          </Typography>
                          <LessonCardDescription>{getLessonDescription(lesson as LESSON_NAME)}</LessonCardDescription>
                        </LessonCardHeader>

                        {totalDuration > 0 && (
                          <LessonCardStats>
                            <LessonWpm>
                              {avgWpm.toFixed()}
                              <sup>wpm</sup>
                            </LessonWpm>
                            <LessonAccuracy>Accuracy: {avgAccuracy.toFixed()}%</LessonAccuracy>
                          </LessonCardStats>
                        )}

                        <StartButton>
                          <Button>
                            {isAiLesson
                              ? aiSubscriptionLoading
                                ? 'Loading...'
                                : isAiSubscriber
                                ? 'Start'
                                : !authLoading && !authUser.id
                                ? 'Sign in'
                                : 'Subscribe'
                              : 'Start'}
                          </Button>
                        </StartButton>
                      </LessonCardContent>
                    </LessonCard>
                  </Link>
                )
              })}
          </LessonsWrapper>
        </WithAds>
      </Wrapper>
    </Layout>
  )
}
